// Phase 45.5 — useHinDuplicateCheck (INGEST-01, D-18).
//
// Watches the canvas HIN field and queries the Task-1 hin-duplicate guard so
// the slim "This HIN is already in your inventory" notice can appear when the
// HIN already exists in the tenant's inventory. Debounced + only fires for a
// plausibly-complete HIN (12 chars) to avoid a request per keystroke. Excludes
// the boat currently being edited.
//
// Fail-soft (D-09): a failed lookup never blocks the canvas — the notice just
// doesn't show. "Use anyway" dismisses it for the session.

import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { aiApi } from '@/modules/inventory/services/aiApi';
import type { BoatFormData } from '@/modules/inventory/types/boat.types';

interface HinDuplicateState {
  /** True when the HIN exists elsewhere in inventory AND not dismissed. */
  show: boolean;
  /** The existing boat id (for the "View existing" link), or null. */
  existingBoatId: string | null;
  /** "Use anyway" — dismiss for the session. */
  dismiss: () => void;
}

// A US boat HIN is 12 chars; only check once the field is plausibly complete.
const HIN_LENGTH = 12;

export function useHinDuplicateCheck(currentBoatId: string | null): HinDuplicateState {
  const { watch } = useFormContext<BoatFormData>();
  // The canvas HIN field (rendered by CanvasFormSections as fieldName="hin_number").
  const hin = watch('hin_number' as keyof BoatFormData) as unknown as string | undefined;

  const [existingBoatId, setExistingBoatId] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const trimmed = (hin ?? '').trim();
    // Reset when the HIN changes — a fresh value gets a fresh check + un-dismiss.
    setExistingBoatId(null);
    setDismissed(false);

    if (trimmed.length < HIN_LENGTH) return;

    let cancelled = false;
    const t = setTimeout(() => {
      aiApi
        .checkHinDuplicate(trimmed, currentBoatId ?? undefined)
        .then((res) => {
          if (cancelled) return;
          setExistingBoatId(res.duplicate ? res.boat_id : null);
        })
        .catch(() => {
          // Fail-soft — no notice on a failed lookup.
          if (!cancelled) setExistingBoatId(null);
        });
    }, 400); // debounce

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [hin, currentBoatId]);

  return {
    show: existingBoatId !== null && !dismissed,
    existingBoatId,
    dismiss: () => setDismissed(true),
  };
}
